import {
  planEnvironmentIssues,
  restrictTaskDesignFreedom,
  spatialContractIssues,
  VISUAL_COMPOSITION_SKILL_SLUG,
  type CapabilityEffect,
  type EnvironmentReading,
  type PlanEnvironmentIssue,
  type TaskDesignResolution,
  type TaskDesignFreedom,
  type TaskPlanProposal,
  type TaskSpatialDecision,
  type TypologyDescriptor,
} from "@calcplan/ai-capabilities";
import { targetIdentityFindings } from "./ai-target-identity";

const NON_ENVIRONMENT_READS = new Set([
  "capability.search",
  "capability.invoke",
  "skill.load",
  "scene.viewport.capture",
  "scene.viewport.review",
  "task.plan.propose",
  "task.plan.progress",
  "task.plan.amend",
  "ui.ask",
]);

export interface DiscoveryGate {
  readyForPlan: boolean;
  forcedTool: "scene.viewport.capture" | "scene.environment.read" | "skill.load" | "catalog.item.search" | "task.plan.propose" | null;
  forcedOverview: boolean;
}

export function minimumProfessionalSkillSlugs(designFreedom: TaskDesignFreedom): string[] {
  // A strict brief removes permission to reinterpret explicit values; it does
  // not remove the need to execute and inspect them through a professional
  // visual method. User-specified constraints remain authoritative.
  void designFreedom;
  return [VISUAL_COMPOSITION_SKILL_SLUG];
}

/**
 * Open and guided design work must be grounded in scene facts, a real viewport
 * image and at least one professional method before planning. Strict work still
 * performs discovery when orchestration requires it, but a skill cannot expand
 * the design freedom granted by the user.
 */
export function discoveryGate(input: {
  designFreedom: TaskDesignFreedom;
  editorScope: boolean;
  evidenceReads: number;
  environmentOverviewAvailable: boolean;
  skillLoads: number;
  skillLoadAttempts: number;
  loadedSkillSlugs?: string[];
  requiredSkillSlugs?: string[];
  catalogEvidenceRequired?: boolean;
  catalogSearches?: number;
  /** A leitura estruturada do ambiente (linhas, função, superfícies) já foi feita neste turno. */
  environmentReadingAvailable?: boolean;
}): DiscoveryGate {
  if (input.evidenceReads < 2) return { readyForPlan: false, forcedTool: null, forcedOverview: false };
  const professionalJudgmentRequired = input.designFreedom !== "strict";
  if (professionalJudgmentRequired && input.editorScope && !input.environmentOverviewAvailable) {
    return { readyForPlan: false, forcedTool: "scene.viewport.capture", forcedOverview: true };
  }
  // Before any guided or open plan, the system reads the room for the model:
  // which lines the walls already carry, what each surface is for, what the
  // room is. Deciding a height without it is how a suspended element ends up
  // on a new line 700 mm away from the one the wall already had.
  if (professionalJudgmentRequired && input.editorScope && input.environmentReadingAvailable === false) {
    return { readyForPlan: false, forcedTool: "scene.environment.read", forcedOverview: false };
  }
  if (professionalJudgmentRequired && input.skillLoads < 1 && input.skillLoadAttempts < 2) {
    return { readyForPlan: false, forcedTool: "skill.load", forcedOverview: false };
  }
  if (professionalJudgmentRequired && input.catalogEvidenceRequired && !input.catalogSearches) {
    return { readyForPlan: false, forcedTool: "catalog.item.search", forcedOverview: false };
  }
  const loaded = new Set(input.loadedSkillSlugs ?? []);
  if ((input.requiredSkillSlugs ?? []).some((slug) => !loaded.has(slug))) {
    return { readyForPlan: false, forcedTool: "skill.load", forcedOverview: false };
  }
  // Discovery is a bounded evidence-gathering phase. Once its contract is met,
  // force the durable plan checkpoint instead of letting the model reopen
  // cameras and reads indefinitely. Plan validation can still request another
  // professional skill explicitly and then force a revised proposal.
  return { readyForPlan: true, forcedTool: "task.plan.propose", forcedOverview: false };
}

/**
 * A spatial contract that no installation can satisfy — a wall-hung upper
 * cabinet that must also rest on the counter, a zero clearance for a
 * suspended element, an upper cabinet "stacked" on a base without the brief
 * asking for a tower — is a hard planning defect regardless of design
 * freedom. Left unchecked it becomes an unsatisfiable law for the executor
 * and the deterministic reviewer, which is exactly how the turn ends up
 * raising and lowering the same module until someone interrupts it.
 */
export function spatialContractPlanIssues(proposal: TaskPlanProposal): string[] {
  return [...new Set(proposal.spatialDecisions.flatMap((decision) => spatialContractIssues({
    subject: decision.subject,
    reference: decision.reference,
    relation: decision.relation,
    support: decision.support,
    clearanceMm: decision.clearanceMm,
    allowContact: decision.allowContact,
    source: decision.source,
  })))];
}

export interface PlanEnvironmentContext {
  reading: EnvironmentReading;
  /** Descritor do sujeito (nome de catálogo, dimensões) quando o plano o identifica por targetCatalogItemId. */
  subjectDescriptor?: (subject: string) => TypologyDescriptor | null;
  /** As palavras do pedido: é contra elas que a identidade de cada alvo é conferida. */
  objective?: string;
}

/**
 * Cota absoluta, linha de referência, faixa de uso, alcance e operação de cada
 * decisão vertical de elemento suspenso, contra a leitura real do ambiente.
 * Vetos voltam ao planejador; objeções voltam com a saída registrada na
 * própria mensagem (datum, justificativa ou premissa).
 */
export function environmentPlanIssues(proposal: TaskPlanProposal, context: PlanEnvironmentContext | null | undefined): PlanEnvironmentIssue[] {
  if (!context) return [];
  const heightOf = (subject: string): number | null => {
    const resolution = proposal.designResolutions.find((candidate) => (
      candidate.targetDimensionsMm?.height !== undefined && decisionsMatch(candidate.subject, subject)
    ));
    return resolution?.targetDimensionsMm?.height ?? null;
  };
  // When the planner did not name the reference element by id, its own review
  // criteria usually did (installation/spacing compare against the reference).
  const referenceOf = (decision: TaskSpatialDecision): string | null => {
    const resolutions = proposal.designResolutions.filter((candidate) => decisionsMatch(candidate.subject, decision.subject));
    const ranked = resolutions.flatMap((resolution) => resolution.reviewCriteria
      .filter((criterion) => criterion.referenceElementId)
      .map((criterion) => ({ id: criterion.referenceElementId!, weight: criterion.aspect === "installation" || criterion.aspect === "spacing" ? 2 : 1 })));
    const scores = new Map<string, number>();
    for (const entry of ranked) scores.set(entry.id, (scores.get(entry.id) ?? 0) + entry.weight);
    const best = [...scores.entries()].sort((left, right) => right[1] - left[1])[0];
    return best?.[0] ?? resolutions.find((resolution) => resolution.targetReferenceElementId)?.targetReferenceElementId ?? null;
  };
  return planEnvironmentIssues(proposal, context.reading, {
    subjectHeightMm: heightOf,
    referenceElementId: referenceOf,
    subjectDescriptor: (subject) => {
      const fromCaller = context.subjectDescriptor?.(subject) ?? null;
      if (fromCaller) return fromCaller;
      const resolution = proposal.designResolutions.find((candidate) => decisionsMatch(candidate.subject, subject));
      return resolution?.targetDimensionsMm
        ? { name: subject, dimensionsMm: { width: resolution.targetDimensionsMm.width ?? 0, height: resolution.targetDimensionsMm.height ?? 0, depth: resolution.targetDimensionsMm.depth ?? 0 } }
        : null;
    },
  });
}

export function professionalPlanReadiness(
  proposal: TaskPlanProposal,
  inferredFreedom: TaskDesignFreedom,
  loadedSkillSlugs: string[],
  environment?: PlanEnvironmentContext | null,
): { ready: boolean; missingSkillSlugs: string[]; issues: string[] } {
  const effectiveFreedom = restrictTaskDesignFreedom(inferredFreedom, proposal.designFreedom);
  if (effectiveFreedom === "strict") {
    const contractIssues = spatialContractPlanIssues(proposal);
    return { ready: contractIssues.length === 0, missingSkillSlugs: [], issues: contractIssues };
  }
  const issues: string[] = [...unresolvedProfessionalDesignIssues(proposal, inferredFreedom)];
  // A decisão vertical de um suspenso é julgada contra o ambiente real: cota
  // absoluta com linha de referência, faixa da atividade, alcance, operação e
  // premissas. Vetos e objeções voltam ao planejador com a saída na mensagem.
  for (const issue of environmentPlanIssues(proposal, environment)) {
    issues.push(`${issue.severity === "veto" ? "[ambiente] " : "[ambiente·objeção] "}${issue.message}`);
  }
  // O pedido descreve o móvel pelo que ele tem; o plano aponta um id. Quando a
  // árvore de componentes do alvo contradiz o descritor (ou não existe e só a
  // foto decide), o achado segue nomeado — consultivo, como tudo aqui.
  if (environment?.objective) {
    for (const finding of targetIdentityFindings({ objective: environment.objective, proposal, reading: environment.reading })) {
      issues.push(`${finding.severity === "objection" ? "[identidade] " : "[identidade·nota] "}${finding.message}`);
    }
  }
  const covered = proposal.skillCoverage.flatMap((entry) => entry.coversFreeVariables);
  const uncovered = proposal.freeVariables.filter((variable) => !covered.some((reference) => decisionsMatch(variable, reference)));
  if (uncovered.length) issues.push(`Variáveis livres sem método profissional: ${uncovered.join("; ")}.`);
  const spatialVariables = proposal.freeVariables.filter(isSpatialVariable);
  if (spatialVariables.length && proposal.spatialDecisions.length === 0) {
    issues.push(`Decisões espaciais sem contrato de suporte e folga: ${spatialVariables.join("; ")}.`);
  }
  for (const decision of proposal.spatialDecisions) {
    if (decision.relation === "stacked" && decision.support !== "element") {
      issues.push(`A relação empilhada de “${decision.subject}” precisa declarar apoio real em outro elemento.`);
    }
    if (decision.allowContact && decision.clearanceMm && decision.clearanceMm.minimum > 0) {
      issues.push(`O contrato de “${decision.subject}” permite contato, mas sua folga mínima é positiva; escolha uma única intenção profissional.`);
    }
    if (!decision.allowContact && decision.clearanceMm?.target === 0) {
      issues.push(`O contrato de “${decision.subject}” proíbe contato, mas sua folga-alvo é zero; escolha uma única intenção profissional.`);
    }
  }
  for (const decision of proposal.decisionAuthorities) {
    if (decision.authority === "explicit" && !["user", "reference", "catalog"].includes(decision.source)) {
      issues.push(`A decisão explícita de “${decision.subject}” não possui fonte explícita comprovável.`);
    }
    if (decision.authority === "agent" && !["catalog", "scene", "skill", "inferred"].includes(decision.source)) {
      issues.push(`A escolha autoral de “${decision.subject}” precisa ter origem no catálogo, na cena, em método profissional ou em inferência contextual.`);
    }
  }
  const hasAgentVisualAuthorship = proposal.designResolutions.some((resolution) => (
    isAgentAuthoredVisualResolution(resolution)
    && ["position", "dimensions", "orientation", "product", "quantity", "relation", "appearance"].includes(resolution.attribute)
  ));
  if (hasAgentVisualAuthorship && proposal.alternativesConsidered.length < 2) {
    issues.push("Compare ao menos duas soluções visualmente distintas antes de executar uma escolha autoral; uma delas pode ser uma variação local e objetiva.");
  }
  const labels = proposal.alternativesConsidered.map((alternative) => alternative.label);
  if (labels.length > 0 && (!proposal.selectedAlternative || !labels.some((label) => decisionsMatch(label, proposal.selectedAlternative!)))) {
    issues.push("Selecione explicitamente uma das alternativas comparadas.");
  }
  const loaded = new Set(loadedSkillSlugs);
  const missingSkillSlugs = [...new Set(proposal.skillCoverage.map((entry) => entry.skillSlug))]
    .filter((slug) => !loaded.has(slug));
  return { ready: issues.length === 0 && missingSkillSlugs.length === 0, missingSkillSlugs, issues };
}

/**
 * Authorship cannot remain as prose to be decided after a module is inserted.
 * These are hard planning issues: unlike wording-only skill coverage, accepting
 * them locally would make the final reviewer design the project retroactively.
 */
export function unresolvedProfessionalDesignIssues(
  proposal: TaskPlanProposal,
  inferredFreedom: TaskDesignFreedom,
): string[] {
  const effectiveFreedom = restrictTaskDesignFreedom(inferredFreedom, proposal.designFreedom);
  // Physical coherence of the spatial contract is not a metadata nicety that
  // may be waived after two attempts: an incoherent contract cannot be
  // executed, so it always returns to the planner with the professional range.
  const contractIssues = spatialContractPlanIssues(proposal);
  if (effectiveFreedom === "strict") return contractIssues;

  const issues: string[] = [...contractIssues];
  if (proposal.freeVariables.length === 0) {
    issues.push("O briefing delega decisões, mas o plano não declarou as variáveis livres; classifique-o como strict somente se todas as propriedades estiverem realmente definidas por fonte explícita.");
    return issues;
  }
  const resolutions = proposal.designResolutions ?? [];
  const stepIds = new Set(proposal.steps.map((step) => step.id));
  const covered = resolutions.flatMap((resolution) => resolution.resolvesFreeVariables);
  const unresolved = proposal.freeVariables.filter((variable) => !covered.some((reference) => decisionsMatch(variable, reference)));
  if (unresolved.length) {
    issues.push(`Variáveis livres ainda sem decisão executável anterior à mutação: ${unresolved.join("; ")}.`);
  }

  for (const variable of proposal.freeVariables) {
    const expectedAttributes = designAttributesIn(variable);
    if (!expectedAttributes.length) continue;
    const matching = resolutions.filter((resolution) => resolution.resolvesFreeVariables.some((reference) => decisionsMatch(variable, reference)));
    const missingAttributes = expectedAttributes.filter((attribute) => !matching.some((resolution) => (
      designResolutionSatisfies(attribute, resolution)
    )));
    if (missingAttributes.length) {
      issues.push(`A variável livre “${variable}” ainda não resolve concretamente: ${missingAttributes.join(", ")}.`);
    }
  }

  for (const resolution of resolutions) {
    if (!stepIds.has(resolution.stepId)) {
      issues.push(`A decisão de “${resolution.subject}” aponta para uma etapa inexistente: ${resolution.stepId}.`);
    }
    if (isDeferredDesignDecision(resolution.decision)) {
      issues.push(`A decisão de “${resolution.subject}” continua adiada; escolha o resultado antes da primeira alteração.`);
    }
    if (resolution.authority === "explicit" && !["user", "reference"].includes(resolution.source)) {
      issues.push(`A resolução explícita de “${resolution.subject}” precisa apontar para o briefing ou para uma referência fornecida.`);
    }
    if (resolution.authority === "derived" && ["user", "reference", "skill"].includes(resolution.source)) {
      issues.push(`A resolução derivada de “${resolution.subject}” deve registrar a evidência técnica que materializa a diretriz (cena, catálogo ou inferência), não autoria profissional.`);
    }
    if (resolution.authority === "agent" && ["user", "reference"].includes(resolution.source)) {
      issues.push(`A resolução autoral de “${resolution.subject}” não pode atribuir ao agente uma escolha já definida pelo briefing ou pela referência.`);
    }
    if (resolution.authority === "agent" && resolution.resolvesFreeVariables.length === 0 && !resolution.authorityNormalized) {
      issues.push(`A resolução autoral de “${resolution.subject}” precisa identificar a variável realmente delegada ao meX.`);
    }
    if (resolution.attribute === "product" && !resolution.targetCatalogItemId) {
      issues.push(`A escolha de produto para “${resolution.subject}” precisa do catalogItemId já selecionado.`);
    }
    if (resolution.attribute === "dimensions" && !resolution.targetDimensionsMm) {
      issues.push(`O dimensionamento de “${resolution.subject}” precisa de targetDimensionsMm já decidido.`);
    }
    if ((resolution.attribute === "material" || resolution.attribute === "appearance")
      && !resolution.targetMaterialCatalogItemId
      && !resolution.targetReferenceElementId) {
      issues.push(`A aparência de “${resolution.subject}” precisa do material de catálogo ou do elemento real usado como referência.`);
    }
    if ((resolution.attribute === "position" || resolution.attribute === "relation")
      && proposal.spatialDecisions.length !== 1
      && !proposal.spatialDecisions.some((decision) => decisionsMatch(decision.subject, resolution.subject))) {
      issues.push(`A implantação de “${resolution.subject}” precisa de uma spatialDecision correspondente.`);
    }
    if (isAgentAuthoredVisualResolution(resolution) && resolution.reviewCriteria.length === 0) {
      issues.push(`A escolha autoral de “${resolution.subject}” para ${resolution.attribute} precisa de critérios profissionais observáveis para execução e revisão.`);
    }
    if (isAgentAuthoredVisualResolution(resolution)
      && !resolution.reviewCriteria.some((criterion) => criterion.evidence === "visual" || criterion.evidence === "geometric")) {
      issues.push(`A escolha autoral de “${resolution.subject}” para ${resolution.attribute} precisa de ao menos uma comparação visual ou geométrica.`);
    }
  }

  const visualVariables = agentAuthoredVisualVariables(proposal);
  if (visualVariables.length) {
    const compositionCoverage = proposal.skillCoverage.find((entry) => entry.skillSlug === VISUAL_COMPOSITION_SKILL_SLUG);
    if (!compositionCoverage) {
      issues.push(`A autoria visual precisa aplicar a skill transversal ${VISUAL_COMPOSITION_SKILL_SLUG}, além da especialidade técnica do objeto ou ambiente.`);
    } else {
      const uncoveredVisualVariables = visualVariables.filter((variable) => (
        !compositionCoverage.coversFreeVariables.some((reference) => decisionsMatch(variable, reference))
      ));
      if (uncoveredVisualVariables.length) {
        issues.push(`A composição visual não cobre todas as decisões livres: ${uncoveredVisualVariables.join("; ")}.`);
      }
    }
  }
  return [...new Set(issues)];
}

function isAgentAuthoredVisualResolution(resolution: TaskDesignResolution): boolean {
  return resolution.authority === "agent"
    && ["position", "dimensions", "orientation", "material", "product", "quantity", "relation", "appearance"].includes(resolution.attribute);
}

function agentAuthoredVisualVariables(proposal: TaskPlanProposal): string[] {
  return [...new Set((proposal.designResolutions ?? [])
    .filter(isAgentAuthoredVisualResolution)
    .flatMap((resolution) => resolution.resolvesFreeVariables))];
}

/**
 * Providers occasionally state an exact catalogue choice in the decision text
 * but omit the duplicated machine fields. Bind only an unambiguous ID/name
 * returned by the catalogue, and derive default dimensions only when the same
 * decision explicitly contains all three catalogue measurements. No product
 * choice or resize is guessed here.
 */
export function normalizeCatalogBackedPlanResolutions(
  proposal: TaskPlanProposal,
  candidates: Array<{
    catalogItemId: string | null;
    id: string | null;
    stableItemId: string | null;
    name: string;
    dimensionsMm: { width: number; height: number; depth: number } | null;
  }>,
): TaskPlanProposal {
  if (!proposal.designResolutions?.length || candidates.length === 0) return proposal;
  const resolutions = proposal.designResolutions.map((resolution) => ({ ...resolution }));
  let authorities = [...proposal.decisionAuthorities];
  let changed = false;

  for (const resolution of [...resolutions]) {
    if (resolution.attribute !== "product") continue;
    const evidence = catalogEvidenceInDecision(resolution.decision, candidates);
    if (!evidence) continue;
    if (!resolution.targetCatalogItemId) {
      resolution.targetCatalogItemId = evidence.catalogItemId ?? evidence.stableItemId ?? evidence.id ?? undefined;
      changed = Boolean(resolution.targetCatalogItemId) || changed;
    }
    const dimensions = evidence.dimensionsMm;
    if (!dimensions || !decisionStatesDimensions(resolution.decision, dimensions)) continue;
    const dimensionVariables = resolution.resolvesFreeVariables.filter((variable) => designAttributesIn(variable).includes("dimensions"));
    if (!dimensionVariables.length || resolutions.some((item) => (
      item.attribute === "dimensions"
      && dimensionVariables.some((variable) => item.resolvesFreeVariables.some((reference) => decisionsMatch(variable, reference)))
    ))) continue;
    resolutions.push({
      stepId: resolution.stepId,
      subject: resolution.subject,
      ...(resolution.instanceKey ? { instanceKey: resolution.instanceKey } : {}),
      attribute: "dimensions",
      authority: resolution.authority,
      resolvesFreeVariables: dimensionVariables,
      decision: `Usar as dimensões catalogadas ${dimensions.width} × ${dimensions.height} × ${dimensions.depth} mm do produto selecionado.`,
      source: "catalog",
      targetDimensionsMm: dimensions,
      reviewCriteria: resolution.reviewCriteria,
      rationale: "As três medidas já foram escolhidas explicitamente na decisão do produto e confirmadas pelo catálogo.",
      verification: "A leitura do elemento confirma largura, altura e profundidade catalogadas.",
    });
    if (!authorities.some((authority) => authority.attribute === "dimensions" && decisionsMatch(authority.subject, resolution.subject))) {
      authorities = [...authorities, {
        subject: resolution.subject,
        attribute: "dimensions",
        authority: "agent",
        source: "catalog",
        requirement: "Aplicar as dimensões exatas já selecionadas e confirmadas no catálogo.",
      }];
    }
    changed = true;
  }

  return changed ? { ...proposal, designResolutions: resolutions, decisionAuthorities: authorities } : proposal;
}

function catalogEvidenceInDecision<T extends {
  catalogItemId: string | null;
  id: string | null;
  stableItemId: string | null;
  name: string;
}>(decision: string, candidates: T[]): T | null {
  const value = normalizeDecision(decision);
  const matches = candidates.filter((candidate) => {
    const identities = [candidate.catalogItemId, candidate.stableItemId, candidate.id]
      .filter((identity): identity is string => Boolean(identity));
    return identities.some((identity) => value.includes(normalizeDecision(identity)))
      || (normalizeDecision(candidate.name).length >= 4 && value.includes(normalizeDecision(candidate.name)));
  });
  return matches.length === 1 ? matches[0]! : null;
}

function decisionStatesDimensions(
  decision: string,
  dimensions: { width: number; height: number; depth: number },
): boolean {
  const values = [dimensions.width, dimensions.height, dimensions.depth];
  return values.every((value) => new RegExp(`(?:^|\\D)${escapeRegExp(String(value))}(?:\\D|$)`).test(decision));
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function designResolutionSatisfies(
  expected: "position" | "dimensions" | "material" | "product" | "relation" | "appearance",
  resolution: TaskDesignResolution,
): boolean {
  const actual = resolution.attribute;
  if (expected === actual) return true;
  // A single catalogue choice can legitimately freeze both identity and size.
  // When both machine targets are present, forcing duplicate prose objects
  // adds no execution safety and was the cause of repeated planning rounds.
  if (expected === "dimensions" && resolution.targetDimensionsMm) return true;
  if (expected === "product" && resolution.targetCatalogItemId) return true;
  // Material and appearance are two representations of the same finish
  // decision when a concrete material/reference target is present. Requiring
  // duplicated resolutions caused the planner to resend the entire plan.
  if ((expected === "material" && actual === "appearance")
    || (expected === "appearance" && actual === "material")) {
    return Boolean(resolution.targetMaterialCatalogItemId || resolution.targetReferenceElementId);
  }
  // Position and relation are two views of the same implantation decision:
  // coordinates/height versus alignment/clearance to a reference. The
  // corresponding spatialDecision remains responsible for the geometric
  // contract, so requiring duplicate prose resolutions adds no safety.
  return (expected === "position" && actual === "relation")
    || (expected === "relation" && actual === "position");
}

function designAttributesIn(variable: string): Array<"position" | "dimensions" | "material" | "product" | "relation" | "appearance"> {
  const value = normalizeDecision(variable);
  // Words such as altura/depth can describe either the size of an object or
  // its implantation. Remove the relational uses before classifying a true
  // dimensional choice, otherwise "installation height" and "depth
  // alignment" incorrectly demand a second dimensions resolution.
  const dimensionalValue = value
    .replace(/\baltura (?:de|da|do) (?:fixacao|instalacao|montagem|posicionamento|implantacao)\b/g, "")
    .replace(/\balinhamento (?:de|da|do|em) (?:largura|altura|profundidade|frente|fundo|eixo)\b/g, "");
  const attributes: Array<"position" | "dimensions" | "material" | "product" | "relation" | "appearance"> = [];
  if (/\b(?:produto|modelo|tipologia|item (?:de catalogo|escolhido|exato))\b/.test(value)) attributes.push("product");
  if (/\b(?:medida|medidas|dimensao|dimensoes|largura|altura|profundidade|tamanho|escala|proporcao)\b/.test(dimensionalValue)) attributes.push("dimensions");
  if (/\b(?:posicao|localizacao|implantacao|disposicao)\b/.test(value)) attributes.push("position");
  if (/\b(?:relacao|alinhamento|folga|vao|apoio|fixacao|encaixe)\b/.test(value)) attributes.push("relation");
  if (/\b(?:material|cor|acabamento|textura)\b/.test(value)) attributes.push("material");
  // "Composição" often names the set being positioned ("lado da
  // composição"), not an additional appearance variable. Treat only words
  // that actually delegate visual language as appearance authorship.
  if (/\b(?:aparencia|linguagem|estilo|estetica|visual)\b/.test(value)) attributes.push("appearance");
  return attributes;
}

function isDeferredDesignDecision(value: string): boolean {
  const normalized = normalizeDecision(value);
  return /\b(?:a definir|a confirmar|pendente|posteriormente|futuramente|durante a execucao|depois de|apos (?:inserir|buscar|pesquisar|encontrar)|quando (?:inserir|buscar|pesquisar|encontrar)|conforme (?:disponivel|encontrado))\b/.test(normalized);
}

/**
 * Plan metadata is an internal control mechanism, not a user decision. Models
 * often describe the same free choice with different wording in
 * freeVariables and skillCoverage. Bind those equivalent execution-time
 * choices to a professional method locally so a wording mismatch cannot stop
 * the project or create another model round-trip.
 */
export function normalizeProfessionalPlanCoverage(
  proposal: TaskPlanProposal,
  loadedSkillSlugs: string[],
): { proposal: TaskPlanProposal; coveredVariables: string[] } {
  const spatialAttributes = new Set(["position", "orientation", "relation"]);
  const resolutionsByStep = new Map<string, TaskDesignResolution[]>();
  const externallyResolvedVariables: string[] = [];
  for (const resolution of proposal.designResolutions ?? []) {
    const entries = resolutionsByStep.get(resolution.stepId) ?? [];
    entries.push(resolution);
    resolutionsByStep.set(resolution.stepId, entries);
  }
  const designResolutions = (proposal.designResolutions ?? []).map((resolution) => {
    const promoted = promoteDerivedAuthorship(proposal, resolution, resolutionsByStep.get(resolution.stepId) ?? []);
    if (promoted !== resolution) return promoted;
    if (resolution.authority !== "agent" || !spatialAttributes.has(resolution.attribute)) return resolution;
    const stepResolutions = resolutionsByStep.get(resolution.stepId) ?? [];
    const pureRelocation = stepResolutions.length > 0
      && stepResolutions.every((candidate) => spatialAttributes.has(candidate.attribute));
    const externallyDirectedRelation = proposal.decisionAuthorities.some((authority) => (
      authority.authority === "explicit"
      && decisionsMatch(authority.subject, resolution.subject)
      && authorityAttributesCompatible(authority.attribute, resolution.attribute)
    ));
    if (!pureRelocation || !externallyDirectedRelation) return resolution;
    externallyResolvedVariables.push(...resolution.resolvesFreeVariables);
    return {
      ...resolution,
      authority: "derived" as const,
      // A demoted resolution is the numeric materialization of an external
      // directive; its evidence is the scene geometry. Keeping the planner's
      // "skill" label made the readiness reject the harness's own transform
      // (20/08 01:44 — six identical rounds while the model groped blind).
      source: resolution.source === "skill" || resolution.source === "user" || resolution.source === "reference"
        ? "scene" as const
        : resolution.source,
      resolvesFreeVariables: [],
      rationale: `${resolution.rationale} A implantação materializa uma relação externa já definida; não constitui redesenho estético desta etapa.`.slice(0, 360),
    };
  });
  let decisionAuthorities = [...proposal.decisionAuthorities];
  for (const resolution of designResolutions) {
    // A derived resolution is only the numerical/geometric realization of an
    // external directive (for example, the center calculated to place an
    // existing object beside another one). It must not be converted back into
    // agent authorship merely because its evidence came from the scene.
    if (resolution.authority === "derived") {
      decisionAuthorities = decisionAuthorities.filter((authority) => !(
        authority.authority === "agent"
        && decisionsMatch(authority.subject, resolution.subject)
        && authorityAttributesCompatible(authority.attribute, resolution.attribute)
      ));
      continue;
    }
    const expectedAuthority = resolution.authority;
    // Both caps are 360 now, so the clamp is a belt for future schema drift,
    // not a truncation the copy relies on (20/08 01:16 — a 331-char decision
    // hit the old 300 cap on re-parse and killed the turn).
    const canonical = {
      subject: resolution.subject.slice(0, 160),
      attribute: resolution.attribute,
      authority: expectedAuthority,
      source: resolution.source,
      requirement: resolution.decision.slice(0, 360),
    };
    const existingIndex = decisionAuthorities.findIndex((authority) => (
      decisionsMatch(authority.subject, resolution.subject)
      && authorityAttributesCompatible(authority.attribute, resolution.attribute)
    ));
    // An externally sourced directive is stronger than a planner's claim of
    // authorship for the numeric realization of that same relation.
    if (existingIndex >= 0 && decisionAuthorities[existingIndex]!.authority === "explicit" && expectedAuthority === "agent") {
      continue;
    }
    if (existingIndex >= 0) decisionAuthorities[existingIndex] = canonical;
    else if (decisionAuthorities.length < 40) decisionAuthorities.push(canonical);
  }
  const normalizedProposal = {
    ...proposal,
    freeVariables: proposal.freeVariables.filter((variable) => !externallyResolvedVariables.some((resolved) => decisionsMatch(variable, resolved))),
    designResolutions,
    decisionAuthorities,
  };
  if (normalizedProposal.designFreedom === "strict" || normalizedProposal.freeVariables.length === 0) {
    return { proposal: normalizedProposal, coveredVariables: [] };
  }

  const skillCoverage = normalizedProposal.skillCoverage.map((entry) => ({
    ...entry,
    coversFreeVariables: [...entry.coversFreeVariables],
  }));
  const loaded = new Set(loadedSkillSlugs);
  const normalizedVariables: string[] = [];
  const visualVariables = agentAuthoredVisualVariables(normalizedProposal);
  if (visualVariables.length && loaded.has(VISUAL_COMPOSITION_SKILL_SLUG)) {
    const compositionIndex = skillCoverage.findIndex((entry) => entry.skillSlug === VISUAL_COMPOSITION_SKILL_SLUG);
    if (compositionIndex >= 0) {
      const coverage = skillCoverage[compositionIndex]!;
      const additions = visualVariables.filter((variable) => (
        !coverage.coversFreeVariables.some((reference) => decisionsMatch(variable, reference))
      ));
      coverage.coversFreeVariables = [...new Set([...coverage.coversFreeVariables, ...additions])].slice(0, 12);
      normalizedVariables.push(...additions);
    } else {
      skillCoverage.push({
        skillSlug: VISUAL_COMPOSITION_SKILL_SLUG,
        coversFreeVariables: visualVariables.slice(0, 12),
        reason: "Camada transversal de composição aplicada às escolhas visuais e espaciais delegadas ao meX.",
      });
      normalizedVariables.push(...visualVariables);
    }
  }
  const covered = skillCoverage.flatMap((entry) => entry.coversFreeVariables);
  const uncovered = normalizedProposal.freeVariables.filter((variable) => !covered.some((reference) => decisionsMatch(variable, reference)));
  if (!uncovered.length) {
    return {
      proposal: normalizedVariables.length ? { ...normalizedProposal, skillCoverage } : normalizedProposal,
      coveredVariables: [...new Set(normalizedVariables)],
    };
  }

  let coverageIndex = skillCoverage.findIndex((entry) => loaded.has(entry.skillSlug));
  if (coverageIndex < 0 && skillCoverage.length > 0) coverageIndex = 0;

  if (coverageIndex >= 0) {
    const coverage = skillCoverage[coverageIndex]!;
    coverage.coversFreeVariables = [...new Set([...coverage.coversFreeVariables, ...uncovered])].slice(0, 12);
  } else if (loadedSkillSlugs[0]) {
    skillCoverage.push({
      skillSlug: loadedSkillSlugs[0],
      coversFreeVariables: uncovered.slice(0, 12),
      reason: "Método profissional já carregado aplicado às escolhas livres deste plano.",
    });
  } else {
    return { proposal: normalizedProposal, coveredVariables: [] };
  }

  return {
    proposal: { ...normalizedProposal, skillCoverage },
    coveredVariables: [...new Set([...normalizedVariables, ...uncovered])],
  };
}

const SPATIAL_FREE_VARIABLE_PATTERN = /\b(?:posicao|implantacao|localizacao|altura|folga|vao|apoio|fixacao|instalacao|relacao|alinhamento|disposicao)\b/;

/**
 * "Derived" means the numeric realization of a directive the briefing already
 * fixed. It never means the height, gap or implantation the planner chose for
 * a new element, and it never resolves a declared free variable — those are
 * authorship by definition. Planners under pressure from the readiness rules
 * relabel such choices as derived (it is the cheapest way to pass), and the
 * label then hides the decision from the visual critic and protects it in the
 * final review. Promote them back to agent authorship deterministically.
 */
export function promoteDerivedAuthorship(
  proposal: TaskPlanProposal,
  resolution: TaskDesignResolution,
  stepResolutions: ReadonlyArray<TaskDesignResolution>,
): TaskDesignResolution {
  if (resolution.authority !== "derived") return resolution;
  if (resolution.resolvesFreeVariables.length > 0) {
    return {
      ...resolution,
      authority: "agent",
      authorityNormalized: "resolves-free-variable",
      rationale: `${resolution.rationale} Resolve uma variável livre do briefing; portanto é escolha autoral revisável, não materialização de diretriz externa.`.slice(0, 360),
    };
  }
  const spatial = resolution.attribute === "position" || resolution.attribute === "relation" || resolution.attribute === "orientation";
  if (!spatial || proposal.designFreedom === "strict") return resolution;
  const createsNewElement = stepResolutions.some((candidate) => (
    candidate !== resolution
    && candidate.attribute === "product"
    && decisionsMatch(candidate.subject, resolution.subject)
  ));
  if (!createsNewElement) return resolution;
  const explicitlyFixed = proposal.decisionAuthorities.some((authority) => (
    authority.authority === "explicit"
    && authority.source !== "scene"
    && decisionsMatch(authority.subject, resolution.subject)
    && authorityAttributesCompatible(authority.attribute, resolution.attribute)
    && /\d/.test(authority.requirement)
  ));
  if (explicitlyFixed) return resolution;
  const relatedVariables = proposal.freeVariables.filter((variable) => {
    const normalized = normalizeDecision(variable);
    return SPATIAL_FREE_VARIABLE_PATTERN.test(normalized) && decisionsMatch(variable, resolution.subject);
  });
  return {
    ...resolution,
    authority: "agent",
    authorityNormalized: "position-of-new-element",
    resolvesFreeVariables: relatedVariables.slice(0, 12),
    rationale: `${resolution.rationale} A implantação de um elemento novo é escolha autoral revisável; o briefing não fixou sua altura ou folga.`.slice(0, 360),
  };
}

function authorityAttributesCompatible(
  left: TaskPlanProposal["decisionAuthorities"][number]["attribute"],
  right: TaskPlanProposal["decisionAuthorities"][number]["attribute"],
): boolean {
  return left === right
    || (left === "position" && right === "relation")
    || (left === "relation" && right === "position");
}

function normalizeDecision(value: string): string {
  return value.normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/\s+/g, " ").trim().toLocaleLowerCase("pt-BR");
}

function decisionsMatch(left: string, right: string): boolean {
  const normalizedLeft = normalizeDecision(left);
  const normalizedRight = normalizeDecision(right);
  if (normalizedLeft === normalizedRight) return true;
  if (normalizedLeft.length >= 8 && normalizedRight.length >= 8
    && (normalizedLeft.includes(normalizedRight) || normalizedRight.includes(normalizedLeft))) return true;
  const leftTerms = decisionTerms(normalizedLeft);
  const rightTerms = decisionTerms(normalizedRight);
  if (!leftTerms.length || !rightTerms.length) return false;
  if (leftTerms.length === 1 && rightTerms.includes(leftTerms[0]!)) return true;
  if (rightTerms.length === 1 && leftTerms.includes(rightTerms[0]!)) return true;
  const shared = leftTerms.filter((term) => rightTerms.includes(term)).length;
  return shared >= 2 && shared / Math.min(leftTerms.length, rightTerms.length) >= 0.6;
}

function decisionTerms(value: string): string[] {
  const ignored = new Set(["a", "as", "o", "os", "de", "da", "das", "do", "dos", "e", "em", "para", "com", "um", "uma"]);
  return [...new Set(value.split(/[^a-z0-9]+/u).filter((term) => term.length > 2 && !ignored.has(term)))];
}

function isSpatialVariable(value: string): boolean {
  const normalized = normalizeDecision(value);
  return /\b(?:posicao|implantacao|localizacao|altura|distancia|folga|vao|relacao|alinhamento|apoio|fixacao|encaixe|disposicao)\b/.test(normalized);
}

export function isDiscoveryEvidenceRead(capabilityName: string, effect: string): boolean {
  return effect === "read" && !NON_ENVIRONMENT_READS.has(capabilityName);
}

/**
 * Atingir o mínimo de evidências libera o planejamento, mas não encerra a
 * descoberta. O modelo ainda pode precisar de catálogo, geometria ou estado de
 * componentes para produzir um plano executável.
 */
export function isDiscoveryCapabilityAvailable(input: {
  name: string;
  effect: CapabilityEffect;
  readyForPlan: boolean;
  designFreedom: TaskDesignFreedom;
}): boolean {
  if (["capability.search", "capability.invoke", "task.plan.progress", "task.plan.amend"].includes(input.name)) return false;
  if (input.name === "task.plan.propose") return input.readyForPlan;
  if (input.name === "skill.load") return input.designFreedom !== "strict";
  return input.effect === "read";
}

/**
 * Discovery may reduce creative freedom, never increase it. Visual acceptance
 * criteria remain attached to the delivery brief, while enforcement belongs to
 * the independent global review after executable steps are registered.
 */
export function applyDesignPolicyToProposal(
  inferredFreedom: TaskDesignFreedom,
  proposal: TaskPlanProposal,
): TaskPlanProposal {
  const discoveredFreedom = proposal.designFreedom === "strict" && proposal.freeVariables.length
    ? "guided"
    : proposal.designFreedom;
  const designFreedom = restrictTaskDesignFreedom(inferredFreedom, discoveredFreedom);
  // Quality assurance is an application gate, not a delivery the model may
  // leave pending. Some providers still append a final "review/conference"
  // step despite the prompt. Remove only a terminal, dependency-complete,
  // non-corrective review step; its criteria remain part of the professional
  // brief and the independent global review runs after all real deliveries.
  const standaloneReviews = proposal.steps.filter((step, index, steps) => isStandaloneFinalQualityStep(step, index, steps));
  const deliverySteps = proposal.steps.filter((step) => !standaloneReviews.includes(step));
  const effectiveSteps = deliverySteps.length ? deliverySteps : proposal.steps;
  const finalStepIndex = Math.max(0, effectiveSteps.length - 1);
  const reviewCriteria = standaloneReviews.flatMap((step) => step.acceptanceCriteria);
  return {
    ...proposal,
    designFreedom,
    creativeCriteria: [...new Set([...proposal.creativeCriteria, ...reviewCriteria])].slice(0, 12),
    steps: effectiveSteps.map((step, index) => ({
      ...step,
      evidence: designFreedom === "strict" || step.evidence.includes("visual") || index !== finalStepIndex
        ? step.evidence
        : [...step.evidence.slice(0, 5), "visual"],
    })),
  };
}

function isStandaloneFinalQualityStep(
  step: TaskPlanProposal["steps"][number],
  index: number,
  steps: TaskPlanProposal["steps"],
): boolean {
  if (steps.length < 2 || index !== steps.length - 1 || !step.evidence.includes("visual")) return false;
  const priorIds = steps.slice(0, -1).map((candidate) => candidate.id);
  if (!priorIds.length || !priorIds.every((id) => step.dependsOn.includes(id))) return false;
  const description = normalizeDecision([
    step.title,
    step.deliverable,
    ...step.acceptanceCriteria,
  ].join(" "));
  const qualityIntent = /\b(?:conferencia|revisao|validacao|auditoria|inspecao|checagem|controle de qualidade|captura|laudo|panorama|qualidade final|quality review|final review)\b/.test(description);
  const correctiveIntent = /\b(?:corrigir|ajustar|mover|reposicionar|inserir|adicionar|remover|substituir|dimensionar|pintar|instalar|criar|modelar|decorar|fechar|abrir)\b/.test(description);
  return qualityIntent && !correctiveIntent;
}
